import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, alpha } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Import your 6 community images
import CommunityImg1 from '../../assets/Community1.jpg';
import CommunityImg2 from '../../assets/Community2.jpg';
import CommunityImg3 from '../../assets/Community3.jpg';
import CommunityImg4 from '../../assets/Community4.jpg';
import CommunityImg5 from '../../assets/Community5.jpg';
import CommunityImg6 from '../../assets/Community6.jpg';

interface CommunitySectionProps {
  isDark: boolean;
}

const CommunitySection: React.FC<CommunitySectionProps> = ({ isDark }) => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [fade, setFade] = useState(true);

  // Community images array
  const communityImages = [
    CommunityImg1,
    CommunityImg2,
    CommunityImg3,
    CommunityImg4,
    CommunityImg5,
    CommunityImg6,
  ];

  // Community main texts array - one for each image
  const communityTexts = [
    "Connect. Collaborate. Create.",
    "Engage, Connect, Thrive",
    "Build Together, Grow Faster",
    "Where Connections Turn Into Opportunities",
    "Learn, Share, Evolve",
    "Connect Minds. Create Impact.",
  ];

  // Community descriptions array - one for each image
  const communityDescriptions = [
    "Join our vibrant community of remote professionals, freelancers, and digital nomads. Discover networking events, skill-sharing sessions, and collaborative opportunities at your favorite cafes.",
    "Create events and network with like-minded professionals. Host workshops, mastermind sessions, and collaborative projects that help you grow both personally and professionally.",
    "Take a break and meet people who share your passion. Enjoy coffee chats, casual meetups, and spontaneous connections that turn into lasting friendships and partnerships.",
    "Transform casual encounters into meaningful collaborations. From brainstorming sessions to joint ventures, find partners who complement your skills and ambitions.",
    "Continuous learning is at our core. Share knowledge, gain new perspectives, and evolve together through skill swaps, mentorship programs, and educational events.",
    "Make an impact through collective intelligence. Join forces with innovators, creators, and thinkers to solve problems and create solutions that matter."
  ];

  // Mobile descriptions array - shortened to 2 lines each
  const mobileDescriptions = [
    "Join remote professionals & digital nomads. Discover networking events & collaborative opportunities at cafes.",
    "Create events & network with professionals. Host workshops & collaborative projects for growth.",
    "Take breaks & meet like-minded people. Enjoy coffee chats & connections that become friendships.",
    "Turn connections into collaborations. Find partners for brainstorming & joint ventures.",
    "Learn & share knowledge together. Evolve through skill swaps & mentorship programs.",
    "Make impact with collective intelligence. Join innovators to solve problems & create solutions."
  ];

  // Theme constants
  const lokiTextGradient = 'linear-gradient(45deg, #C4B66F 10%, #44A08D 90%)'; 
  const lightModeTextGradient = 'linear-gradient(135deg, #051814 0%, #1D5C4A 100%)';
  const discordFont = {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  };

  const handleSectionClick = () => {
    navigate('/community');
  };

  useEffect(() => {
    const interval = setInterval(() => {
      // Fade out current text
      setFade(false);
      
      // Change image and text after fade out
      setTimeout(() => {
        setCurrentImageIndex((prev) => (prev + 1) % communityImages.length);
        setFade(true);
      }, 500);
    }, 5000); // Change every 5 seconds

    return () => clearInterval(interval);
  }, [communityImages.length]);

  return (
    <Box
      sx={{
        minHeight: { xs: 'auto', md: '100vh' },
        display: 'flex',
        alignItems: { xs: 'flex-end', md: 'center' },
        justifyContent: { xs: 'flex-start', md: 'center' },
        position: 'relative',
        overflow: 'hidden',
        backgroundImage: `url(${communityImages[currentImageIndex]})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        transition: 'background-image 0.5s ease-in-out',
        cursor: { xs: 'pointer', md: 'default' }, // Pointer cursor on mobile only
        '&:hover': {
          '& .mobile-click-overlay': {
            opacity: { xs: 0.05, md: 0 }, // Slight darken on hover for mobile
          }
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          background: isDark 
            ? 'linear-gradient(90deg, rgba(5, 24, 20, 0.85) 0%, rgba(5, 24, 20, 0.6) 100%)'
            : 'linear-gradient(90deg, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.6) 100%)',
          zIndex: 0
        }
      }}
      onClick={handleSectionClick}
      role="button"
      tabIndex={0}
      aria-label="Navigate to community page"
    >
      {/* Mobile click overlay - visible feedback */}
      <Box 
        className="mobile-click-overlay"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: isDark ? '#000' : '#fff',
          opacity: 0,
          transition: 'opacity 0.3s ease',
          zIndex: 1,
          pointerEvents: 'none',
          display: { xs: 'block', md: 'none' },
        }}
      />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
        <Box sx={{ 
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          py: { xs: 4, md: 10 },
          position: 'relative',
          zIndex: 1,
          gap: { xs: 3, md: 8 },
          minHeight: { xs: '400px', md: 'auto' },
          mt: { xs: 'auto', md: 0 },
          mb: { xs: 4, md: 0 },
        }}>
          {/* Content - Bottom-left on mobile, left on desktop */}
          <Box sx={{ 
            flex: 1,
            textAlign: { xs: 'left', md: 'left' },
            maxWidth: { xs: '90%', md: '50%' },
          }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '1.5rem', sm: '2rem', md: '4rem', lg: '4.5rem' },
                lineHeight: 1.1,
                mb: { xs: 2, md: 4 },
                textTransform: 'uppercase',
                ...discordFont,
                background: isDark ? lokiTextGradient : lightModeTextGradient,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                opacity: fade ? 1 : 0,
                transform: fade ? 'translateY(0)' : 'translateY(20px)',
                transition: 'all 0.5s ease-in-out',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: { xs: '-8px', md: '-20px' },
                  left: { xs: '0', md: '0' },
                  width: { xs: '60px', md: '150px' },
                  height: '3px',
                  background: lokiTextGradient,
                  borderRadius: '2px',
                  display: { xs: 'block', md: 'block' },
                }
              }}
            >
              {communityTexts[currentImageIndex]}
            </Typography>

            {/* Desktop description */}
            <Typography
              variant="h6"
              sx={{
                mb: { xs: 3, md: 6 },
                color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(5, 24, 20, 0.9)',
                fontSize: { xs: '0rem', md: '1.25rem' },
                fontWeight: 400,
                lineHeight: 1.6,
                opacity: fade ? 1 : 0,
                transform: fade ? 'translateY(0)' : 'translateY(20px)',
                transition: 'all 0.5s ease-in-out 0.1s',
                display: { xs: 'none', md: 'block' },
              }}
            >
              {communityDescriptions[currentImageIndex]}
            </Typography>

            {/* Mobile description - 2 lines only */}
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(5, 24, 20, 0.9)',
                fontSize: '0.85rem',
                fontWeight: 400,
                lineHeight: 1.4,
                opacity: fade ? 1 : 0,
                transform: fade ? 'translateY(0)' : 'translateY(20px)',
                transition: 'all 0.5s ease-in-out 0.1s',
                display: { xs: 'block', md: 'none' },
              }}
            >
              {mobileDescriptions[currentImageIndex]}
            </Typography>

            {/* Desktop Button - Hidden on mobile */}
            <Button
              variant="contained"
              size="large"
              onClick={(e) => {
                e.stopPropagation(); // Prevent triggering parent click
                navigate('/community');
              }}
              endIcon={<ArrowForward sx={{ fontSize: { xs: '0.9rem', md: '1.5rem' } }} />}
              sx={{
                minWidth: { xs: 'auto', sm: '200px' },
                px: { xs: 2.5, sm: 5 },
                py: { xs: 1.2, sm: 2 },
                borderRadius: '100px',
                background: isDark 
                  ? `linear-gradient(135deg, rgba(5, 24, 20, 0.8) 0%, rgba(15, 59, 46, 0.8) 100%)`
                  : lightModeTextGradient,
                color: isDark ? '#C4B66F' : 'white', 
                fontSize: { xs: '0.8rem', sm: '1.1rem' },
                textTransform: 'none',
                backdropFilter: 'blur(10px)',
                border: isDark ? '1px solid rgba(196, 182, 111, 0.3)' : 'none',
                ...discordFont,
                opacity: fade ? 1 : 0,
                transform: fade ? 'translateY(0)' : 'translateY(20px)',
                transition: 'all 0.5s ease-in-out 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 10px 20px ${alpha('#0C3B2E', 0.5)}`,
                },
                whiteSpace: 'nowrap',
                display: { xs: 'none', md: 'inline-flex' }, // Hidden on mobile
              }}
            >
              Explore Community
            </Button>
          </Box>

          {/* Right Side - Empty space for image background (desktop only) */}
          <Box sx={{ 
            flex: 1,
            display: { xs: 'none', md: 'block' },
            minHeight: '400px',
          }} />
        </Box>

        {/* Image indicators */}
        <Box 
          sx={{ 
            display: 'flex',
            justifyContent: { xs: 'flex-start', md: 'center' },
            gap: 1,
            mt: { xs: 0, md: 0 },
            mb: { xs: 2, md: 0 },
            ml: { xs: 2, md: 0 },
            position: { xs: 'relative', md: 'absolute' },
            bottom: { xs: 'auto', md: 40 },
            left: 0,
            right: 0,
            zIndex: 1,
          }}
          onClick={(e) => e.stopPropagation()} // Prevent indicator clicks from navigating
        >
          {communityImages.map((_, index) => (
            <Box
              key={index}
              onClick={(e) => {
                e.stopPropagation(); // Stop event bubbling
                setFade(false);
                setTimeout(() => {
                  setCurrentImageIndex(index);
                  setFade(true);
                }, 500);
              }}
              sx={{
                width: { xs: '6px', md: '10px' },
                height: { xs: '6px', md: '10px' },
                borderRadius: '50%',
                bgcolor: index === currentImageIndex 
                  ? (isDark ? '#C4B66F' : '#051814')
                  : (isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(5, 24, 20, 0.3)'),
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: index === currentImageIndex 
                    ? (isDark ? '#D4C67F' : '#0F3B2E')
                    : (isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(5, 24, 20, 0.5)'),
                },
              }}
            />
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default CommunitySection;